import 'immer';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import type {
  Order,
  OrderSearchOutput,
  OrderSearchInput,
} from '@repo/api-contract';
import ApiClient from '../../util/api-client';
type SearchBy = OrderSearchInput['searchBy'];

export interface OrderSearchState {
  searchInput: string;
  openOrderSearchModal: boolean;
  resp?: OrderSearchOutput;
  searchBy: SearchBy;
  loading: boolean;
  searchLoading: boolean;
  selectedOrders: Order[];
  multiSelect: boolean;
  onImport?: (selectedOrders: Order[]) => void;
  onCancel?: () => void;
}

// Initialize state
const initialState: OrderSearchState = {
  searchInput: '',
  openOrderSearchModal: false,
  resp: undefined,
  searchBy: 'orderCode',
  loading: false,
  searchLoading: false,
  multiSelect: false,
  selectedOrders: [],
  onImport: undefined,
  onCancel: undefined,
};

export const OrderSearchModalSlice = createSlice({
  name: 'OrderSearch',
  initialState,
  reducers: {
    openSearchOrderModal: (
      state,
      action: PayloadAction<{
        onImport?: (selectedOrders: Order[]) => void;
        onCancel?: () => void;
        multiSelect?: boolean;
      }>,
    ) => ({
      ...initialState,
      openOrderSearchModal: true,
      onImport: action.payload.onImport,
      onCancel: action.payload.onCancel,
      multiSelect: action.payload.multiSelect || state.multiSelect,
    }),
    closeModal: (state) => ({
      ...state,
      openOrderSearchModal: false,
    }),
    setSearchInput: (state, action: PayloadAction<string>) => ({
      ...state,
      searchInput: action.payload,
    }),
    setSearchBy: (state, action: PayloadAction<SearchBy>) => ({
      ...state,
      searchBy: action.payload,
    }),
    setResults: (
      state,
      action: PayloadAction<OrderSearchOutput | undefined>,
    ) => ({
      ...state,
      resp: action.payload,
    }),
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setSearchLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      searchLoading: action.payload,
    }),
    setSelectedOrders: (state, action: PayloadAction<Order[]>) => {
      state.selectedOrders = action.payload;
    },
  },
});

export const {
  openSearchOrderModal,
  closeModal,
  setSearchInput,
  setSearchBy,
  setResults,
  setLoading,
  setSearchLoading,
  setSelectedOrders,
} = OrderSearchModalSlice.actions;
export default OrderSearchModalSlice.reducer;

export const searchOrders =
  (searchInput: OrderSearchInput) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(setSearchLoading(true));
      const resp = await ApiClient.searchOrders.query(searchInput);
      dispatch(setResults(resp));
    } catch (error: unknown) {
      dispatch(setResults(undefined));
    } finally {
      dispatch(setSearchLoading(false));
    }
  };
